import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const AgreementReacknowledgementModal = ({ onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreementText, setAgreementText] = useState("");
  const [agreementVersion, setAgreementVersion] = useState("");
  const [previousVersion, setPreviousVersion] = useState("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  useEffect(() => {
    fetchAgreementStatus();
  }, []);

  const fetchAgreementStatus = async () => {
    try {
      // Get status
      const statusRes = await axios.get(
        `${API}/legal/photographer-agreement/status`,
        { withCredentials: true }
      );
      
      if (!statusRes.data.needs_reacknowledgement) {
        // No re-acknowledgement needed
        onComplete?.();
        return;
      }

      setPreviousVersion(statusRes.data.accepted_version || "none");
      
      // Get current version info
      const versionRes = await axios.get(`${API}/legal/photographer-agreement/current-version`);
      const version = versionRes.data.version;
      setAgreementVersion(version);

      // Get agreement text
      const textRes = await axios.get(`${API}/legal/photographer-agreement/${version}`);
      setAgreementText(textRes.data.content);
    } catch (error) {
      console.error("Failed to check agreement status:", error);
      // If there's an error, don't block the user
      onComplete?.();
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    const scrolledToBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!agreed) {
      toast.error("Please agree to the updated Photographer Platform Agreement");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/legal/photographer-agreement/accept`,
        { version: agreementVersion },
        { withCredentials: true }
      );

      toast.success("Agreement accepted!");
      onComplete?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept agreement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return null;
  }

  // If no agreement text, modal shouldn't show
  if (!agreementText) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="agreement-reacknowledge-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-3xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-border bg-muted/30">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h2 className="text-2xl font-serif mb-1">Updated Agreement</h2>
                <p className="text-muted-foreground">
                  Our Photographer Platform Agreement has been updated. Please review and accept the new version to continue.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Previous version: {previousVersion} → New version: {agreementVersion}
                </p>
              </div>
            </div>
          </div>

          {/* Agreement Text */}
          <div className="border-b border-border">
            <ScrollArea 
              className="h-[350px] p-6"
              onScrollCapture={handleScroll}
            >
              <div className="prose prose-invert prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                  {agreementText}
                </pre>
              </div>
            </ScrollArea>
          </div>

          {/* Footer */}
          <div className="p-6 space-y-4">
            {/* Scroll hint */}
            {!hasScrolledToBottom && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pb-2">
                <FileText className="w-4 h-4" />
                <span>Please scroll to read the entire agreement</span>
              </div>
            )}

            {/* Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg border border-border">
              <Checkbox
                id="reacknowledge-agreement"
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-1"
                data-testid="reacknowledge-checkbox"
              />
              <Label
                htmlFor="reacknowledge-agreement"
                className="text-base font-medium cursor-pointer"
              >
                I agree to the Photographer Platform Agreement
              </Label>
            </div>

            {/* Accept Button */}
            <Button
              onClick={handleAccept}
              disabled={!agreed || saving}
              className="w-full btn-primary"
              size="lg"
              data-testid="reacknowledge-accept-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                "Accept and Continue"
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              You must accept the updated agreement to continue using the platform.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AgreementReacknowledgementModal;
